import type { Conversation } from '../types';
import { isImageAttachment } from '@/lib/messageAttachment';

interface Props {
  conversations: Conversation[];
  selectedId: number | null;
  loading: boolean;
  search: string;
  onSearch: (value: string) => void;
  onSelect: (id: number) => void;
}

// Dernière pièce jointe du fil, pour repérer un message avec image d'un coup d'œil.
function lastAttachment(c: Conversation): { url: string; name: string; type: string } | null {
  for (let i = c.messages.length - 1; i >= 0; i -= 1) {
    const m = c.messages[i];
    if (m.attachment_url) {
      return { url: m.attachment_url, name: m.attachment_name || '', type: m.attachment_type || '' };
    }
  }
  return null;
}

function shortTime(iso: string): string {
  const d = new Date(iso);
  const now = new Date();
  const sameDay = d.toDateString() === now.toDateString();
  if (sameDay) return d.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' });
  return d.toLocaleDateString('fr-FR', { day: 'numeric', month: 'short' });
}

/**
 * Liste des conversations (une par client). S'affiche à gauche de la boîte
 * de réception ; chaque ligne montre le dernier message + le nombre de non-lus.
 */
export default function ConversationList({ conversations, selectedId, loading, search, onSearch, onSelect }: Props) {
  return (
    <div className="flex flex-col w-full lg:w-80 flex-shrink-0">
      <div className="relative mb-3">
        <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-foreground-400 text-sm"></i>
        <input
          type="text"
          value={search}
          onChange={(e) => onSearch(e.target.value)}
          placeholder="Rechercher un client..."
          className="w-full pl-9 pr-3 py-2.5 bg-background-50 border border-background-200/70 rounded-full text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-accent-300"
        />
      </div>

      <div className="flex-1 overflow-y-auto space-y-1.5 max-h-[60vh] lg:max-h-[calc(100vh-260px)]">
        {loading ? (
          <div className="flex items-center justify-center py-16">
            <i className="ri-loader-4-line animate-spin text-2xl text-accent-500"></i>
          </div>
        ) : conversations.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-16 text-center text-foreground-400 px-4">
            <i className="ri-chat-3-line text-3xl mb-2"></i>
            <p className="text-sm">Aucune conversation</p>
            <p className="text-xs mt-1">Les messages privés de vos clients apparaîtront ici.</p>
          </div>
        ) : (
          conversations.map((c) => {
            const name = c.contact?.name || `Client #${c.contactId}`;
            const active = c.contactId === selectedId;
            const att = lastAttachment(c);
            const attIsImg = att ? isImageAttachment(att.type, att.name) : false;
            return (
              <button
                key={c.contactId}
                type="button"
                onClick={() => onSelect(c.contactId)}
                className={`w-full flex items-start gap-3 p-3 rounded-lg text-left transition-colors cursor-pointer border ${
                  active
                    ? 'bg-accent-100/70 border-accent-200'
                    : 'bg-background-50 border-background-200/70 hover:bg-background-100/70'
                }`}
              >
                <div className="w-10 h-10 rounded-full bg-accent-100 flex items-center justify-center overflow-hidden flex-shrink-0">
                  {c.contact?.image ? (
                    <img src={c.contact.image} alt="" className="w-full h-full object-cover" />
                  ) : (
                    <span className="text-sm font-bold text-accent-700">{name.charAt(0).toUpperCase()}</span>
                  )}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center justify-between gap-2">
                    <span className="text-sm font-semibold text-foreground-900 truncate">{name}</span>
                    <span className="text-[10px] text-foreground-400 whitespace-nowrap flex-shrink-0">{shortTime(c.lastAt)}</span>
                  </div>
                  <div className="flex items-center gap-1.5 mt-0.5">
                    {att && (
                      attIsImg ? (
                        <img
                          src={att.url}
                          alt={att.name || 'Photo'}
                          className="w-8 h-8 rounded-md object-cover flex-shrink-0 border border-background-200/70"
                          onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }}
                        />
                      ) : (
                        <span className="w-8 h-8 rounded-md bg-secondary-100 text-secondary-700 flex items-center justify-center flex-shrink-0">
                          <i className="ri-attachment-2 text-xs"></i>
                        </span>
                      )
                    )}
                    <p className="text-xs text-foreground-500 truncate">{c.lastMessage}</p>
                  </div>
                </div>
                {c.unread > 0 && (
                  <span className="w-5 h-5 rounded-full bg-primary-500 text-background-50 text-[10px] font-bold flex items-center justify-center flex-shrink-0">
                    {c.unread}
                  </span>
                )}
              </button>
            );
          })
        )}
      </div>
    </div>
  );
}